import React, { useState, useEffect } from 'react';
import { tenantService, complianceService, legalQaService } from '../../services/api';

interface EditTenantModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    tenant: any;
}

export default function EditTenantModal({ isOpen, onClose, onSuccess, tenant }: EditTenantModalProps) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        rentAmount: '',
        propertyId: '',
        status: 'active',
        tenancyEndDate: '',
        rentPaymentDay: '',
        freePeriodGranted: false,
        freePeriodDays: '',
    });
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingProps, setLoadingProps] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tenantFull, setTenantFull] = useState<any>(null);
    const [changeLogOpen, setChangeLogOpen] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [ackSaving, setAckSaving] = useState(false);

    useEffect(() => {
        if (tenant && isOpen) {
            setFormData({
                name: tenant.name || '',
                email: tenant.email || '',
                phone: tenant.phone || '',
                rentAmount: tenant.rentAmount?.toString() || '',
                propertyId: tenant.propertyId || '',
                status: tenant.status || 'active',
                tenancyEndDate: tenant.tenancyEndDate || '',
                rentPaymentDay: tenant.rentPaymentDay?.toString() || '',
                freePeriodGranted: tenant.freePeriodGranted || false,
                freePeriodDays: tenant.freePeriodDays?.toString() || '',
            });
            setTenantFull(tenant);
            setAcknowledged(false);
            fetchProperties();
            // Acknowledge check uses localStorage so the banner stays dismissed per-device
            const ackKey = `rent_payment_ack_${tenant.id}`;
            setAcknowledged(localStorage.getItem(ackKey) === 'true');
        }
    }, [tenant, isOpen]);

    const fetchProperties = async () => {
        setLoadingProps(true);
        try {
            const res = await complianceService.getProperties();
            setProperties(res.data?.data || res.data || []);
        } catch (err) {
            console.error('Failed to fetch properties:', err);
        } finally {
            setLoadingProps(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            await tenantService.updateTenant(tenant.id, {
                ...formData,
                rentAmount: formData.rentAmount ? parseFloat(formData.rentAmount) : undefined,
                rentPaymentDay: formData.rentPaymentDay ? parseInt(formData.rentPaymentDay, 10) : undefined,
                freePeriodGranted: formData.freePeriodGranted,
                freePeriodDays: formData.freePeriodDays ? parseInt(formData.freePeriodDays, 10) : undefined,
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Update error:', err);
            setError(err.response?.data?.message || 'Failed to update tenant');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                            <i className="fas fa-user-edit text-blue-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Edit Tenant Details
                            </h3>
                            <div className="mt-2 text-left">
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {error && (
                                        <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                                            {error}
                                        </div>
                                    )}
                                    
                                    {/* Property Selection (Connection) */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-bold text-emerald-700 mb-1 tracking-tight">CONNECTED PROPERTY</label>
                                        <div className="relative">
                                            <select
                                                required
                                                className="block w-full border-2 border-emerald-50 rounded-lg shadow-sm p-3 focus:ring-emerald-500 focus:border-emerald-500 bg-emerald-50 font-bold text-gray-700 appearance-none"
                                                value={formData.propertyId}
                                                onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
                                            >
                                                <option value="">-- Select Property --</option>
                                                {properties.map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {typeof p.address === 'object' ? p.address.line1 : p.address} ({p.postcode || (typeof p.address === 'object' ? p.address.postcode : '')})
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-emerald-400">
                                                <i className="fas fa-chevron-down text-xs"></i>
                                            </div>
                                        </div>
                                        {loadingProps && <p className="text-[10px] text-emerald-500 animate-pulse mt-1 font-bold italic">Refreshing portfolio...</p>}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                            <input
                                                type="text"
                                                required
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={formData.email}
                                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                                            <input
                                                type="tel"
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={formData.phone}
                                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Status</label>
                                            <select
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={formData.status}
                                                onChange={e => setFormData({ ...formData, status: e.target.value, tenancyEndDate: e.target.value === 'ended' ? formData.tenancyEndDate : '' })}
                                            >
                                                <option value="active">Active</option>
                                                <option value="ended">Ended</option>
                                                <option value="pending">Pending</option>
                                            </select>
                                        </div>
                                        {formData.status === 'ended' && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Tenancy End Date</label>
                                                <input
                                                    type="date"
                                                    required
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.tenancyEndDate}
                                                    onChange={e => setFormData({ ...formData, tenancyEndDate: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Rent Amount (£)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 font-bold"
                                            value={formData.rentAmount}
                                            onChange={e => setFormData({ ...formData, rentAmount: e.target.value })}
                                            placeholder="1200.00"
                                        />
                                    </div>

                                    {/* Rent Payment Date Section (E-016 / F-042) */}
                                    <div className="pt-4 border-t border-gray-100">
                                        <div className="flex items-center gap-2 mb-4">
                                            <i className="fas fa-calendar-alt text-blue-600"></i>
                                            <h4 className="text-sm font-bold text-gray-800">Rent Payment Date</h4>
                                        </div>

                                        {/* Amber Warning Banner — shown when payment day differs from start day */}
                                        {tenantFull?.tenancyStartDate && formData.rentPaymentDay && (
                                            (() => {
                                                const startDay = new Date(tenantFull.tenancyStartDate).getDate();
                                                const paymentDay = parseInt(formData.rentPaymentDay, 10);
                                                if (startDay !== paymentDay && !acknowledged) {
                                                    return (
                                                        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                                            <div className="flex items-start gap-3">
                                                                <i className="fas fa-exclamation-triangle text-amber-600 mt-0.5"></i>
                                                                <div className="flex-1">
                                                                    <p className="font-bold text-amber-900 text-sm">Rent Payment Date Differs from Tenancy Start Date</p>
                                                                    <p className="text-xs text-amber-800 mt-1">
                                                                        The rent payment day ({paymentDay}{['st','nd','rd','th'][Math.min((paymentDay % 10) - 1, 3)] || 'th'}) is different from the tenancy start date day ({startDay}{['st','nd','rd','th'][Math.min((startDay % 10) - 1, 3)] || 'th'}). 
                                                                        This is common when aligning rent to a tenant's payday. Ensure your tenancy agreement reflects the correct payment date.
                                                                    </p>
                                                                    <div className="flex items-center gap-3 mt-3">
                                                                        <a
                                                                            href="#"
                                                                            onClick={async (e) => {
                                                                                e.preventDefault();
                                                                                try {
                                                                                    const res = await legalQaService.ask(
                                                                                        'What are the legal requirements when setting a rent payment date that differs from the tenancy start date in an assured shorthold tenancy in England?'
                                                                                    );
                                                                                    const data = res.data?.data || res.data;
                                                                                    const answer = data.answer || data.message || 'Please consult your legal advisor.';
                                                                                    alert(`Legal Q&A:\n\n${answer}`);
                                                                                } catch {
                                                                                    alert('Legal Q&A service unavailable. Please consult your legal advisor.');
                                                                                }
                                                                            }}
                                                                            className="text-xs font-bold text-amber-700 hover:text-amber-900 underline inline-flex items-center gap-1"
                                                                        >
                                                                            <i className="fas fa-gavel"></i>
                                                                            Check Legal Q&A
                                                                        </a>
                                                                        <button
                                                                            onClick={async () => {
                                                                                setAckSaving(true);
                                                                                const ackKey = `rent_payment_ack_${tenant.id}`;
                                                                                localStorage.setItem(ackKey, 'true');
                                                                                setAcknowledged(true);
                                                                                setAckSaving(false);
                                                                            }}
                                                                            className="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1 rounded-md hover:bg-amber-200 inline-flex items-center gap-1"
                                                                        >
                                                                            {ackSaving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                                                            Acknowledge
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()
                                        )}

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600">Payment Day of Month</label>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="28"
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={formData.rentPaymentDay}
                                                        onChange={e => setFormData({ ...formData, rentPaymentDay: e.target.value })}
                                                    />
                                                    <span className="text-xs text-gray-500 whitespace-nowrap">of each month</span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600">Free Period</label>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <input
                                                        type="checkbox"
                                                        id="editFreePeriod"
                                                        checked={formData.freePeriodGranted}
                                                        onChange={e => setFormData({ ...formData, freePeriodGranted: e.target.checked })}
                                                        className="h-4 w-4 text-emerald-600 rounded"
                                                    />
                                                    <label htmlFor="editFreePeriod" className="text-xs text-gray-600">Free period granted</label>
                                                </div>
                                                {formData.freePeriodGranted && (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="90"
                                                        className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Free days"
                                                        value={formData.freePeriodDays}
                                                        onChange={e => setFormData({ ...formData, freePeriodDays: e.target.value })}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Immutable Change Log */}
                                        {tenantFull?.rentPaymentDayHistory?.length > 0 && (
                                            <div className="mt-4">
                                                <button
                                                    type="button"
                                                    onClick={() => setChangeLogOpen(!changeLogOpen)}
                                                    className="text-xs font-medium text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                                                >
                                                    <i className={`fas fa-chevron-${changeLogOpen ? 'down' : 'right'} text-[10px]`}></i>
                                                    Payment Date Change Log ({tenantFull.rentPaymentDayHistory.length} change{tenantFull.rentPaymentDayHistory.length !== 1 ? 's' : ''})
                                                </button>
                                                {changeLogOpen && (
                                                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                                                        {tenantFull.rentPaymentDayHistory.map((entry: any, idx: number) => (
                                                            <div key={idx} className="flex items-center justify-between text-xs text-gray-600 border-b border-gray-100 pb-1 last:border-0">
                                                                <span className="font-medium text-gray-800">
                                                                    Day {entry.previousDay || '—'} → Day {entry.newDay}
                                                                </span>
                                                                <span className="text-gray-400">
                                                                    {new Date(entry.changedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        ))}
                                                        <p className="text-[10px] text-gray-400 italic pt-1">All changes are logged immutably for audit purposes.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse space-x-reverse space-x-3">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2 bg-emerald-600 text-base font-bold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:w-auto sm:text-sm disabled:opacity-50"
                                        >
                                            {loading ? 'Saving...' : 'Save Connections'}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
